import { createFileRoute } from "@tanstack/react-router";
import {
	Bot,
	CalendarClock,
	Image as ImageIcon,
	Layers,
	Sparkles,
	Video,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/for/creators";

export const Route = createFileRoute(path)({
	component: ForCreators,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher for Creators - AI Cross-Posting at Scale",
			description:
				"Creators post everywhere. AgentReacher cross-posts to Instagram, TikTok, YouTube Shorts, X, and Threads with platform-specific tone and timing.",
		}),
});

function ForCreators() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="For creators"
				title="Post once. Reach every audience."
				description="Drop one upload and AgentReacher ships native versions to Instagram, TikTok, YouTube, and X. Per-platform captions, peak-time scheduling, zero copy-paste."
			/>

			<MarketingSection
				title="Built for the creator workflow"
				description="Solo creators and small media teams deserve agency-grade tools without agency pricing."
			>
				<FeatureGrid
					features={[
						{
							icon: Video,
							title: "Vertical video everywhere",
							description:
								"One Reel publishes natively to Instagram, TikTok, and YouTube Shorts.",
						},
						{
							icon: ImageIcon,
							title: "Carousels and pins",
							description:
								"Same idea ships to Instagram carousels and Pinterest, with descriptions tuned per platform.",
						},
						{
							icon: Bot,
							title: "Per-platform captions",
							description:
								"The agent rewrites per network: TikTok gets hooks, Instagram gets longer copy, X gets a thread.",
						},
						{
							icon: CalendarClock,
							title: "Smart timing per platform",
							description:
								"Each network has different peak hours. The agent schedules each version separately.",
						},
						{
							icon: Layers,
							title: "Repurpose long-form",
							description:
								"YouTube videos get auto-suggested cuts for Shorts, Reels, and TikTok.",
						},
						{
							icon: Sparkles,
							title: "Drive from chat",
							description:
								"'Cross-post the new tutorial to all my platforms.' Done in 30 seconds.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="A typical creator workflow" align="left">
				<ol className="mx-auto max-w-2xl space-y-3 text-sm leading-relaxed text-muted-foreground">
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							1
						</span>
						<span>Connect Instagram, TikTok, YouTube, and Threads.</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							2
						</span>
						<span>
							Drop one upload. The agent generates per-platform titles,
							captions, and tags.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							3
						</span>
						<span>
							Approve. Each platform schedules into its own peak window.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							4
						</span>
						<span>
							Publish goes live across every channel without manual reposting.
						</span>
					</li>
				</ol>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						Pro covers most creators. Max if you run multiple channels or a
						podcast network.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
